import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import * as authApi from '@/services/authApi';
import GlobalNavOverlay from '@/components/GlobalNavOverlay';

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // First check if we have user data from navigation state
      if (location.state?.user) {
        console.log('ProtectedRoute: Using user from navigation state');
        setUser(location.state.user);
        setIsAuthenticated(true);
        setIsChecking(false);
        return;
      }

      // Check if we have a token in localStorage
      const token = localStorage.getItem('session_token');
      const savedUser = localStorage.getItem('user');
      
      console.log('ProtectedRoute: Checking auth, token exists:', !!token);
      
      if (!token) {
        console.log('ProtectedRoute: No token found, redirecting to login');
        setIsAuthenticated(false);
        setIsChecking(false);
        return;
      }

      // If we have a saved user, use it immediately while verifying with server
      if (savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setIsAuthenticated(true);
          console.log('ProtectedRoute: Using cached user data');
        } catch (e) {
          console.error('ProtectedRoute: Error parsing saved user');
        }
      }

      // Verify with server
      try {
        const userData = await authApi.getMe();
        console.log('ProtectedRoute: Server verified user:', userData.name);
        setUser(userData);
        setIsAuthenticated(true);
        // Update cached user data
        localStorage.setItem('user', JSON.stringify(userData));
      } catch (error) {
        console.error('ProtectedRoute: Auth check failed:', error.response?.status);
        // If server returns 401, the session is invalid - must re-login
        if (error.response?.status === 401) {
          console.log('ProtectedRoute: Session expired, clearing and redirecting');
          localStorage.removeItem('session_token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
        } else if (!savedUser) {
          // Only redirect if we don't have a valid cached user and it's not a 401
          setIsAuthenticated(false);
        }
        // For other errors (network, etc.), keep cached user if available
      }
      
      setIsChecking(false);
    };

    checkAuth();
  }, [location.pathname]); // Only re-check when path changes, not on every render

  // Loading state
  if (isChecking && isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#2D5F4F] mx-auto mb-4" />
          <p className="text-[#5A5F6E]">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (isAuthenticated === false) {
    return <Navigate to="/login" replace />;
  }

  // Require admin but user is not admin (Super Admin OR Site Admin pass).
  if (requireAdmin && !['Admin', 'super_admin', 'Site Admin'].includes(user?.role)) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Authenticated - global top bar (Home + Active Site + Request Vehicle) above the page, then the page
  return (
    <>
      <GlobalNavOverlay />
      {children}
    </>
  );
};

export default ProtectedRoute;